import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// --- Types ---
export interface CartItem {
  id: number;
  name: string;
  image: string;
  price: number;
  quantity: number;
}

interface CartContextType {
  cart: CartItem[];
  cartItemCount: number;
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // 1. Local Storage se data load karein (Agar pehle se save hai)
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const savedCart = localStorage.getItem('tradyFrogCart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error("Failed to load cart from storage", error);
      return [];
    }
  });

  // 2. Jab bhi Cart change ho, usse Local Storage me save karein
  useEffect(() => {
    localStorage.setItem('tradyFrogCart', JSON.stringify(cart));
  }, [cart]);

  // --- Add Function ---
  const addToCart = (item: CartItem) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((cartItem) => cartItem.id === item.id);
      
      // Agar item pehle se hai, to quantity badhao
      if (existingItem) {
        return prevCart.map((cartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      }
      
      // Naya item add karein (Price ko number me convert karke)
      const newItem = { 
          ...item, 
          price: Number(item.price), // Ensure price is a number
          quantity: 1 
      };
      return [...prevCart, newItem];
    });
  };

  // --- Remove Function ---
  const removeFromCart = (id: number) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== id));
  };

  // --- Clear Function ---
  const clearCart = () => {
    setCart([]);
  };

  // Total items count (quantity based)
  const cartItemCount = cart.reduce((count, item) => count + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, cartItemCount, addToCart, removeFromCart, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};